/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { VinylAmznPlayer } from '@amzn/vinyl-amzn'
import { getAuth } from '@/auth/authRef'
import { createDmlsOptions } from '@/customer/createDmlsOptions'
import { getDeviceConfig } from '@/device/deviceConfig'
import { instrument } from '@/core/perf'
import { createDisposer, type Unsubscribe } from '@amazon/vinyl-util'
import { AuthStatus } from '@/auth/auth'
import { getVideoElement } from '@/video/videoStage'

/**
 * Creates a VinylAmznPlayer instance asynchronously using code splitting.
 * This allows top-level views to begin their first service requests before the player is loaded.
 *
 * Uses the singleton `<video>` element so the same vinyl player can
 * play audio (default behaviour, video element with no visual stage)
 * and music videos (when the element is mounted into a stage by the
 * video detail page). Vinyl already does the right thing for
 * audio-only sources on a `<video>` element — it just doesn't render
 * picture data — so a single player covers both content shapes.
 *
 * @returns A promise that resolves to the configured VinylAmznPlayer instance
 */
export const createVinylPlayer = instrument(
    'createVinylPlayer',
    async (): Promise<VinylAmznPlayer> => {
        // Dynamically import the vinyl-amzn module
        const vinyl = await import('@amzn/vinyl-amzn')

        const player = vinyl.createVinylAmznPlayer({ media: getVideoElement() })
        player.configure({
            streaming: getDeviceConfig().streamingOptions,
            errorLoggingService: {
                endpoint: vinyl.VinylErrorLoggingEndpoints.VINYL_DEV,
            },
        })

        // For easier debugging. Without this you can still use `vinylGlobal.player`.(window as any).player = player
        return player
    }
)

export function initializePlayerAuthBinding(player: VinylAmznPlayer): Unsubscribe {
    const disposer = createDisposer()
    const { add, dispose } = disposer
    const auth = getAuth()
    add(
        auth.status.onData((status) => {
            if (status === AuthStatus.SIGNED_IN) {
                void (async () => {
                    if (!disposer.disposed) {
                        player.configure({
                            dmls: await createDmlsOptions(player),
                        })
                    }
                })()
            } else {
                player.clearTrackCache()
                player.configure({
                    dmls: null,
                })
            }
        })
    )
    return dispose
}
